import { DEFAULT_ID } from 'prose-core'
import { Node } from 'prosemirror-model'
import { NodeSelection } from 'prosemirror-state'
import { Decoration, EditorView, NodeView } from 'prosemirror-view'
import * as React from 'react'
import { useContext, useLayoutEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { ViewEditableContext } from 'screens/ProseEdit/components/ViewEditableContextProvider/ViewEditableContextProvider'
import { v4 } from 'uuid'
import { type IReactNodeViewContext, ReactNodeViewContext, type ViewGetPosition } from './ReactNodeViewContext'

export class ReactNodeView implements NodeView {
  componentRef: React.RefObject<HTMLDivElement>
  contentDomContainerRef: React.RefObject<HTMLDivElement>
  containerElementType?: string
  dom?: HTMLElement
  contentDOM?: HTMLElement
  component: React.FC<React.PropsWithChildren<any>>
  node: Node
  view: EditorView
  getPos: () => number
  decorations: Decoration[]
  setContextState?: React.Dispatch<React.SetStateAction<IReactNodeViewContext>>
  onDestroy?: (key: string) => void
  inCommentMode: boolean
  viewKey = v4()
  isSelectedInit: boolean

  constructor(
    node: Node,
    view: EditorView,
    getPos: ViewGetPosition,
    decorations: Decoration[],
    component: React.FC<React.PropsWithChildren<any>>,
    onDestroy: (key: string) => void,
    inCommentMode: boolean,
    containerElementType?: string,
  ) {
    this.node = node
    this.view = view
    this.getPos = getPos as () => number
    this.decorations = decorations
    this.component = component
    this.componentRef = React.createRef()
    this.contentDomContainerRef = React.createRef()
    this.onDestroy = onDestroy
    this.inCommentMode = inCommentMode
    this.isSelectedInit =
      this.view.state.selection instanceof NodeSelection && this.view.state.selection.node === this.node
    this.containerElementType = containerElementType
  }

  init() {
    this.dom = document.createElement(this.containerElementType || (this.node.isInline ? 'span' : 'div'))
    this.dom.classList.add('ProseMirror__dom')
    this.dom.dataset.nodeType = this.node.type.name

    if (!this.node.isLeaf && !this.node.isAtom) {
      this.contentDOM = document.createElement('div')
      // contenteditable should always have the value of view.editable
      // however, view.editable is edited async, so we can't just define
      // contenteditable once and be done with it. So what we do is to
      // initialize it here, and then in renderPortal we listen to
      // view.editable (via useLayoutEffect) and update contenteditable on change
      this.contentDOM.setAttribute('contenteditable', 'true')
      this.contentDOM.classList.add('ProseMirror__contentDOM')
      this.dom.appendChild(this.contentDOM)
    }

    return {
      nodeView: this,
      portal: this.renderPortal(this.dom),
    }
  }

  selectNode() {
    this.setContextState && this.setContextState((existingState: any) => ({ ...existingState, isSelected: true }))
  }

  deselectNode() {
    this.setContextState && this.setContextState((existingState: any) => ({ ...existingState, isSelected: false }))
  }

  renderPortal(container: HTMLElement) {
    const Component: React.FC<React.PropsWithChildren<unknown>> = (props) => {
      /**
       * The context comes from this useState because the context is local to this specific instance of the nodeView,
       * and thus cannot be "global" or "shared"
       */
      const [contextState, setContextState] = useState<IReactNodeViewContext>({
        node: this.node,
        view: this.view,
        getPos: this.getPos as ViewGetPosition,
        decorations: this.decorations,
        nodeView: this,
        inCommentMode: this.inCommentMode,
        isSelected: this.isSelectedInit,
      })
      this.setContextState = setContextState

      useLayoutEffect(() => {
        if (this.contentDomContainerRef != null && this.contentDOM != null) {
          this.contentDomContainerRef.current?.appendChild(this.contentDOM)
        }
      }, [])

      // These 2 vars are only used in the following useLayoutEffect and only
      // defined to appease the linter who does not like to have
      // [this.contentDom, this.view.editable] as dependencies of the useLayoutEffect
      const contentDOM = this.contentDOM
      const { editable } = useContext(ViewEditableContext) ?? {
        // default to contenteditable=true in test env so we don't have to bother waiting for the view to be ready
        editable: process.env.NODE_ENV === 'test' ? true : false,
      }
      // The following block of code is to always keep `contenteditable` in sync
      // with view.editable: Since we manually created the container div (in init),
      // we also have to listen to changes on view.editable and update
      // contenteditable accordingly.
      useLayoutEffect(() => {
        if (contentDOM) {
          contentDOM.setAttribute('contenteditable', `${editable}`)
        }
      }, [contentDOM, editable])

      return (
        <ReactNodeViewContext.Provider value={contextState}>
          <this.component {...props} ref={!this.node.isLeaf ? this.contentDomContainerRef : undefined} />
        </ReactNodeViewContext.Provider>
      )
    }
    return ReactDOM.createPortal(<Component />, container, this.viewKey)
  }

  update(node: Node) {
    if (!node || node.type !== this.node.type) {
      /**
       * This nodeView has changed to be something else, therefore we need to tell
       * prosemirror that this node is "unable to update itself" and must be redrawn
       *
       * We do this by returning false
       */
      return false
    } else if (
      this.node.attrs.blockId !== node.attrs.blockId &&
      node.attrs.blockId !== DEFAULT_ID &&
      this.node.attrs.blockId !== DEFAULT_ID
    ) {
      // If the node is of the same type but a different instance, we force it to be reparsed and recreated in order to
      // reset the internal React state. This is added to fix React nodeviews that keep an internal state. But we don't
      // check whether this nodeview has an internal state (because it's hard), so this might end up affecting stateless
      // nodeviews.
      return false
    } else {
      if (this.setContextState) {
        this.setContextState((existingState: any) => ({ ...existingState, node: node }))
      }
    }
    return true
  }

  rerender() {
    // force-update the context by passing a new object with the same keys
    // (same data, different identity), which triggers a subtree update
    if (this.setContextState) {
      this.setContextState((existingState: any) => ({ ...existingState }))
    }
  }

  destroy() {
    const oldDom = this.dom
    this.dom = undefined
    this.contentDOM = undefined
    this.onDestroy?.(this.viewKey)
    oldDom?.remove()
  }

  ignoreMutation(p: MutationRecord | { type: 'selection'; target: Element }): boolean {
    return true
  }
}

export interface TCreateReactNodeView extends Omit<IReactNodeViewContext, 'nodeView' | 'isSelected' | 'inCommentMode'> {
  inCommentMode?: boolean
  component: React.FC<React.PropsWithChildren<any>>
  onCreatePortal: (portal: any) => void
  onDestroyPortal: (key: string) => void
  containerElementType?: string
}

export const createReactNodeView = ({
  node,
  view,
  getPos,
  decorations,
  component,
  onCreatePortal,
  onDestroyPortal,
  inCommentMode = false,
  containerElementType,
}: TCreateReactNodeView) => {
  const reactNodeView = new ReactNodeView(
    node,
    view,
    getPos,
    decorations,
    component,
    onDestroyPortal,
    inCommentMode,
    containerElementType,
  )
  const { nodeView, portal } = reactNodeView.init()
  onCreatePortal(portal)

  return nodeView
}
